import type { ButtonHTMLAttributes } from 'react'
import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { de } from '@/lib/format'
import { cn } from '@/lib/utils'

type ButtonProps = ButtonHTMLAttributes<HTMLButtonElement>

/**
 * Der Hauptknopf. Bewusst gross und mit sichtbarem Druckpunkt: bedient wird
 * mit dem Daumen, oft im Stehen.
 */
export function PrimaryButton({ className, ...props }: ButtonProps) {
    return (
        <button
            type="button"
            {...props}
            className={cn(
                'w-full rounded-2xl bg-wb-accent px-5 py-4 text-base font-extrabold text-[#0a0d14]',
                'shadow-[0_6px_0_var(--color-wb-accent-lo)] transition',
                'active:translate-y-[3px] active:shadow-[0_3px_0_var(--color-wb-accent-lo)]',
                'disabled:opacity-40 disabled:shadow-none',
                className,
            )}
        />
    )
}

export function GhostButton({ className, ...props }: ButtonProps) {
    return (
        <button
            type="button"
            {...props}
            className={cn(
                'w-full rounded-2xl border-[1.5px] border-wb-border bg-transparent px-5 py-3.5',
                'text-sm font-bold text-wb-text transition active:bg-wb-panel-hi',
                'disabled:opacity-40',
                className,
            )}
        />
    )
}

interface OddsChipProps extends ButtonProps {
    label: string
    value: number
    selected?: boolean
}

/**
 * Eine Quote zum Antippen.
 *
 * Die Beschriftung bricht nie um: Sonst stünde die Zahl in einem Knopf tiefer
 * als in den anderen beiden, und die drei Quoten lägen nicht auf einer Linie.
 * Auf schmalen Handys schrumpft die Schrift deshalb mit der Knopfbreite, damit
 * das längste Wort („Unentschieden“) Abstand zum Rand behält; ab einem normal
 * breiten Handy bleibt sie bei 10 px. Alle drei Knöpfe sind gleich breit und
 * haben damit immer dieselbe Schriftgröße.
 */
export function OddsChip({ label, value, selected = false, className, ...props }: OddsChipProps) {
    return (
        <button
            type="button"
            aria-pressed={selected}
            {...props}
            className={cn(
                '@container flex min-w-0 flex-1 flex-col items-center gap-1 rounded-[14px] border-[1.5px] px-2 py-3 transition',
                selected
                    ? '-translate-y-px animate-wb-rise border-wb-accent bg-wb-accent text-[#0a0d14] shadow-[0_0_0_4px_rgb(255_138_26/0.2)]'
                    : 'border-wb-border bg-wb-panel-hi text-wb-text',
                className,
            )}
        >
            <span
                className={cn(
                    'text-[length:min(10px,12.5cqw)] leading-tight font-bold tracking-[.04em] whitespace-nowrap',
                    selected ? 'opacity-70' : 'opacity-55',
                )}
            >
                {label}
            </span>
            <span className="wb-num text-xl leading-none font-extrabold">{de(value)}</span>
        </button>
    )
}

/**
 * Ein Knopf, der noch nicht gedrückt werden kann, weil die Lehrkraft noch
 * nicht weitergeschaltet hat.
 *
 * Die laufenden Punkte sind kein Zierrat: sie zeigen, dass das Handy lebt und
 * nicht hängt - die häufigste Sorge in einer Klasse mit 30 Geräten.
 */
export function LockedButton({ children }: { children: React.ReactNode }) {
    const { t } = useTranslation()
    const [dots, setDots] = useState(0)

    useEffect(() => {
        const timer = setInterval(() => setDots((value) => (value + 1) % 4), 600)

        return () => clearInterval(timer)
    }, [])

    return (
        <div
            role="status"
            aria-label={t('common.waitingForTeacher')}
            className="flex w-full items-center justify-center gap-2.5 rounded-[14px] border-[1.5px] border-dashed border-wb-border bg-wb-panel-lo px-4 py-4 text-center text-sm font-bold text-wb-dim"
        >
            <span>{children}</span>
            <span className="w-3.5 text-left wb-num opacity-70">{'.'.repeat(dots)}</span>
        </div>
    )
}

/** Eine Taste des Ziffernblocks für den Beitritts-Code. */
export function PadKey({ className, ...props }: ButtonProps) {
    return (
        <button
            type="button"
            {...props}
            className={cn(
                'flex h-14 items-center justify-center rounded-2xl border border-wb-border bg-wb-panel',
                'text-2xl font-extrabold text-wb-text transition active:scale-95 active:bg-wb-panel-hi',
                'disabled:opacity-30',
                className,
            )}
        />
    )
}
